import React from 'react';
import { X } from 'lucide-react';
import Button from '../../components/ui/Button';
import { FOCUS } from '../../components/ui/tokens';
import DateInput from "../../components/DateInput";
import {
  DUE_CHIP_OPTIONS,
  SORT_OPTIONS,
  activeFilterKeys,
  type PayableFilters,
} from './payableFilters';

/**
 * The bill list's filters (D49 phase 2): quick due-date chips, due and invoice date ranges, an
 * amount range, currency, and the sort. It only edits the values; the server applies every one
 * of them, and the summary cards above the list follow the same filter.
 *
 * Pulled out of AccountsPage on purpose (a god file, strangled not grown). It is controlled:
 * the page owns the state, so a link from a report can open it pre-filled.
 */

export interface PayableFilterBarProps {
  filters: PayableFilters;
  onChange: (patch: Partial<PayableFilters>) => void;
  onClear: () => void;
}

const FIELD_INPUT =
  'h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20';

const CURRENCY_CHOICES = ['TTD', 'USD'];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>
      {children}
    </label>
  );
}

/** The filters a report link sets that have no control of their own, said in words, each removable. */
function linkedFilterPills(filters: PayableFilters): Array<{ key: string; label: string; clear: Partial<PayableFilters> }> {
  const pills: Array<{ key: string; label: string; clear: Partial<PayableFilters> }> = [];
  if (filters.supplierExact) {
    pills.push({
      key: 'supplierExact',
      label: filters.supplier === '' ? 'Bills with no supplier name' : `Exactly this supplier: ${filters.supplier}`,
      clear: { supplierExact: false },
    });
  }
  if (filters.held === 'only') {
    pills.push({ key: 'held', label: 'Only bills on hold or disputed', clear: { held: '' } });
  }
  if (filters.held === 'exclude') {
    pills.push({ key: 'held', label: 'Leaving out bills on hold or disputed', clear: { held: '' } });
  }
  if (filters.openOnly) {
    pills.push({ key: 'openOnly', label: 'Only bills that still owe money', clear: { openOnly: false } });
  }
  return pills;
}

export const PayableFilterBar: React.FC<PayableFilterBarProps> = ({ filters, onChange, onClear }) => {
  const currencies = CURRENCY_CHOICES.includes(filters.currency) || filters.currency === ''
    ? CURRENCY_CHOICES
    : [...CURRENCY_CHOICES, filters.currency];
  const pills = linkedFilterPills(filters);
  const anyActive = activeFilterKeys(filters).length > 0 || filters.supplierExact;

  return (
    <section aria-label="Filter bills" className="mb-4 space-y-3">
      <div role="group" aria-label="Due date" className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-xs font-medium text-slate-600">Due:</span>
        {DUE_CHIP_OPTIONS.map((chip) => {
          const active = filters.due === chip.value;
          return (
            <button
              key={chip.value}
              type="button"
              aria-pressed={active}
              onClick={() => onChange({ due: active ? '' : chip.value })}
              className={[
                'inline-flex h-7 items-center rounded-md border px-3 text-xs font-medium transition-colors',
                active
                  ? 'border-primary-500 bg-primary-500 text-white'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                FOCUS,
              ].join(' ')}
            >
              {chip.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 items-end gap-3 sm:grid-cols-4">
        <Field label="Due from">
          <DateInput validity="filter"
            value={filters.dueFrom}
            onChange={(v) => onChange({ dueFrom: v })}
            className={FIELD_INPUT}
          />
        </Field>
        <Field label="Due to">
          <DateInput validity="filter"
            value={filters.dueTo}
            onChange={(v) => onChange({ dueTo: v })}
            className={FIELD_INPUT}
          />
        </Field>
        <Field label="Invoice from">
          <DateInput validity="filter"
            value={filters.invoiceFrom}
            onChange={(v) => onChange({ invoiceFrom: v })}
            className={FIELD_INPUT}
          />
        </Field>
        <Field label="Invoice to">
          <DateInput validity="filter"
            value={filters.invoiceTo}
            onChange={(v) => onChange({ invoiceTo: v })}
            className={FIELD_INPUT}
          />
        </Field>
        <Field label="Outstanding at least">
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={filters.minOutstanding}
            onChange={(e) => onChange({ minOutstanding: e.target.value })}
            placeholder="0.00"
            className={FIELD_INPUT}
          />
        </Field>
        <Field label="Outstanding at most">
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={filters.maxOutstanding}
            onChange={(e) => onChange({ maxOutstanding: e.target.value })}
            placeholder="No limit"
            className={FIELD_INPUT}
          />
        </Field>
        <Field label="Currency">
          <select
            value={filters.currency}
            onChange={(e) => onChange({ currency: e.target.value })}
            className={FIELD_INPUT}
          >
            <option value="">All currencies</option>
            {currencies.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </Field>
        <div className="flex min-w-0 items-end gap-2">
          <Field label="Sort by">
            <select
              value={filters.sort}
              onChange={(e) => onChange({ sort: e.target.value as PayableFilters['sort'] })}
              className={FIELD_INPUT}
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>
          <Button
            size="sm"
            className="mb-0.5 shrink-0"
            aria-label={filters.dir === 'asc' ? 'Ascending order. Switch to descending' : 'Descending order. Switch to ascending'}
            onClick={() => onChange({ dir: filters.dir === 'asc' ? 'desc' : 'asc' })}
          >
            {filters.dir === 'asc' ? 'Ascending' : 'Descending'}
          </Button>
        </div>
      </div>

      {pills.length > 0 || anyActive ? (
        <div className="flex flex-wrap items-center gap-2">
          {pills.map((pill) => (
            <span
              key={pill.key}
              className="inline-flex items-center gap-1 rounded-full border border-primary-500/40 bg-primary-50 py-0.5 pl-3 pr-1 text-xs font-medium text-primary-600"
            >
              {pill.label}
              <button
                type="button"
                aria-label={`Remove filter: ${pill.label}`}
                onClick={() => onChange(pill.clear)}
                className={`inline-flex h-5 w-5 items-center justify-center rounded-full hover:bg-primary-100 ${FOCUS}`}
              >
                <X aria-hidden="true" className="h-3 w-3" />
              </button>
            </span>
          ))}
          {anyActive ? (
            <Button size="sm" variant="ghost" onClick={onClear}>
              Clear filters
            </Button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export default PayableFilterBar;
